<template>
  <!-- Sem registro tardio a seção NÃO existe: um "nenhum item" permanente no
       Detalhes do Plantão treina o auditor a ignorar o bloco. -->
  <div
    v-if="rows.length"
    class="flex flex-col gap-2 text-left"
    data-testid="post-station-frozen-records"
  >
    <div class="flex items-baseline justify-between gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-neutral50">
        Registros recebidos depois do fechamento
      </p>
      <span class="text-xs text-neutral50">{{ rows.length }} {{ itemWord }}</span>
    </div>

    <!-- A sidebar tem 550px; a tabela tem 5 colunas. O scroll horizontal é do
         container, não da página. -->
    <div class="w-full overflow-x-auto">
      <table class="w-full min-w-[560px] text-left text-xs">
        <thead>
          <tr class="text-neutral50">
            <th v-for="header in HEADERS" :key="header" class="font-semibold py-2 pr-3">
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <tr
            v-for="row in rows"
            :key="row.containerId"
            :data-testid="`post-station-frozen-record-${row.containerId}`"
            class="align-top"
          >
            <td class="py-2 pr-3">
              <div class="flex flex-col gap-0.5">
                <span class="text-textPrimary">
                  {{ ACTIVITY_RECORD_TITLE }}
                  <strong>#{{ row.containerId }}</strong>
                </span>
                <span class="text-neutral50">{{ row.subtitle }}</span>
                <button
                  type="button"
                  class="self-start text-primary30 underline"
                  :data-testid="`post-station-frozen-record-${row.containerId}-view`"
                  @click="emit('view-record', row.containerId)"
                >
                  Ver registro
                </button>
              </div>
            </td>
            <td class="py-2 pr-3 text-textPrimary">{{ row.legLabel }}</td>
            <td class="py-2 pr-3 text-textPrimary">{{ row.declaredAt }}</td>
            <td class="py-2 pr-3 text-textPrimary">{{ row.auditLabel }}</td>
            <td
              class="py-2 text-neutral50"
              :data-testid="`post-station-frozen-record-effect-${row.containerId}`"
            >
              {{ FROZEN_RECORD_EFFECT_TEXT }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  COVERED_LEGS_LABELS,
  CoveredLegs,
  DS_ATTENDANCE_STATUS_LABELS,
  DSAttendanceStatus
} from '@/constants/ds-attendance.constants'
import type { StationRegistrationActivityRecord } from '@/Network/Services/StationService'
import { formatDayMonthInOffset, formatHourMinuteInOffset } from '@/utils/date'

import { FROZEN_RECORD_EFFECT_TEXT } from '../constants'

/**
 * Q28 / M5 seção 02 — "Registros recebidos depois do fechamento".
 *
 * Aprovar o pós-plantão congela o horário efetivo. Os registros que chegarem
 * depois continuam existindo, continuam sendo auditados normalmente e aparecem
 * AQUI, com o carimbo de que não mexem no número que foi para pagamento. Quem
 * quiser incorporá-los reabre o pós-plantão (reversão, com a permissão que já
 * existe) — esta tabela só mostra, não decide.
 */
const props = defineProps<{
  records: StationRegistrationActivityRecord[]
  /** Fuso do plantão (`-03:00`); vazio cai no padrão do produto em `utils/date`. */
  timezone?: string | null
}>()

const emit = defineEmits<{
  'view-record': [containerId: number]
}>()

const HEADERS = ['Registro', 'Perna', 'Horário declarado', 'Auditoria', 'Efeito']

const ACTIVITY_RECORD_TITLE = 'Registro de Atividade · Jornada'

const DISPLAY_FALLBACK = '—'

const timezone = computed(() => props.timezone || '')

const formatHour = (iso?: string | null): string =>
  (iso && formatHourMinuteInOffset(iso, timezone.value)) || DISPLAY_FALLBACK

const formatDay = (iso?: string | null): string | null =>
  (iso && formatDayMonthInOffset(iso, timezone.value)) || null

// O horário declarado é o da perna que o registro cobre; `BOTH` mostra os dois.
const declaredFor = (record: StationRegistrationActivityRecord): string => {
  if (record.coveredLegs === CoveredLegs.CHECKIN) return formatHour(record.declaredCheckin)
  if (record.coveredLegs === CoveredLegs.CHECKOUT) return formatHour(record.declaredCheckout)
  const checkin = formatHour(record.declaredCheckin)
  const checkout = formatHour(record.declaredCheckout)
  if (checkin === DISPLAY_FALLBACK && checkout === DISPLAY_FALLBACK) return DISPLAY_FALLBACK
  return `${checkin} → ${checkout}`
}

// Status cru desconhecido cai no próprio código em vez de sumir: o auditor
// precisa saber que existe algo ali que esta versão da tela não sabe nomear.
const auditLabelFor = (record: StationRegistrationActivityRecord): string => {
  const statusLabel =
    DS_ATTENDANCE_STATUS_LABELS[record.status as DSAttendanceStatus] ?? record.status
  const day = formatDay(record.reviewedAt)
  return day ? `${statusLabel} em ${day}` : statusLabel
}

const subtitleFor = (record: StationRegistrationActivityRecord): string => {
  const attachments = record.attachments?.length ?? 0
  const parts: string[] = []
  if (attachments > 0) parts.push(`${attachments} ${attachments === 1 ? 'anexo' : 'anexos'}`)
  if (record.activityTypeLabel) parts.push(record.activityTypeLabel)
  return parts.length ? parts.join(' · ') : DISPLAY_FALLBACK
}

const rows = computed(() =>
  props.records.map((record) => ({
    containerId: record.containerId,
    subtitle: subtitleFor(record),
    legLabel: record.coveredLegs
      ? (COVERED_LEGS_LABELS[record.coveredLegs as CoveredLegs] ?? record.coveredLegs)
      : DISPLAY_FALLBACK,
    declaredAt: declaredFor(record),
    auditLabel: auditLabelFor(record)
  }))
)

const itemWord = computed(() => (rows.value.length === 1 ? 'item' : 'itens'))
</script>
